import { lazy, Suspense, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import AuthHealthMonitor from "@/components/AuthHealthMonitor";
import KeepaliveMonitor from "@/components/KeepaliveMonitor";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

// EAGER — most users land here first. Keeping them in the main bundle
// avoids a Suspense flash on first paint of the most common entry points.
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";

// LAZY — route-level code splitting (Jun 4 2026 perf audit, item #1).
// Each of these becomes its own webpack chunk and is fetched on-demand
// the first time a user navigates to that route. Drops landing bundle
// from ~918 KB → an estimated ~280-350 KB. Subsequent navigations within
// the same session reuse the already-loaded chunks (no re-fetch).
const Features        = lazy(() => import("@/pages/Features"));
const Pricing         = lazy(() => import("@/pages/Pricing"));
const About           = lazy(() => import("@/pages/About"));
const FAQ             = lazy(() => import("@/pages/FAQ"));
const Contact         = lazy(() => import("@/pages/Contact"));
const ForgotPassword  = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword   = lazy(() => import("@/pages/ResetPassword"));
const Privacy         = lazy(() => import("@/pages/Privacy"));
const DeleteAccount   = lazy(() => import("@/pages/DeleteAccount"));
const Signup          = lazy(() => import("@/pages/Signup"));
const VerifyEmail     = lazy(() => import("@/pages/VerifyEmail"));
const Onboarding      = lazy(() => import("@/pages/Onboarding"));
const AuthCallback    = lazy(() => import("@/pages/AuthCallback"));
const NotFound        = lazy(() => import("@/pages/NotFound"));

// Bhrigu Academy — Wave Ārdrā (Jun 4 2026). Cinematic encounter
// engine + first nakshatra. Lazy-loaded so the canvas + 8-beat data
// add zero weight to the main bundle.
const Learn           = lazy(() => import("@/pages/learn/Learn"));
const LearnArdra      = lazy(() => import("@/pages/learn/Ardra"));
const Academy         = lazy(() => import("@/pages/Academy"));
const AcademyMoon     = lazy(() => import("@/pages/academy/Moon"));
const AcademyAscendant = lazy(() => import("@/pages/academy/Ascendant"));
const AcademySun      = lazy(() => import("@/pages/academy/Sun"));
const AcademyDasha    = lazy(() => import("@/pages/academy/Dasha"));
const AcademySani     = lazy(() => import("@/pages/academy/Sani"));
const AcademyMars     = lazy(() => import("@/pages/academy/Mars"));
const AcademyJupiter  = lazy(() => import("@/pages/academy/Jupiter"));
const AcademyVenus    = lazy(() => import("@/pages/academy/Venus"));
const AcademyMercury  = lazy(() => import("@/pages/academy/Mercury"));
const AcademyNodes    = lazy(() => import("@/pages/academy/Nodes"));
const AcademyLessonLayout = lazy(() => import("@/components/AcademyLessonLayout"));
const Vakya           = lazy(() => import("@/pages/app/vakya/Vakya"));
const Poster          = lazy(() => import("@/pages/Poster"));
const PosterVakya     = lazy(() => import("@/pages/PosterVakya"));

// Lazy — authenticated app surface. Behind <Protected>, so anonymous
// visitors never touch these chunks at all.
const AppShell        = lazy(() => import("@/pages/app/AppShell"));
const Dashboard       = lazy(() => import("@/pages/app/Dashboard"));
const ChartView       = lazy(() => import("@/pages/app/ChartView"));
const AskV2           = lazy(() => import("@/pages/app/AskV2"));
const Talk            = lazy(() => import("@/pages/app/Talk"));
const Journal         = lazy(() => import("@/pages/app/Journal"));
const Profiles        = lazy(() => import("@/pages/app/Profiles"));
const Billing         = lazy(() => import("@/pages/app/Billing"));
const Admin           = lazy(() => import("@/pages/Admin"));

// Shared Suspense fallback — matches existing "Aligning..." aesthetic
// used by Protected. Zero new UI to maintain.
const RouteFallback = () => (
  <div className="min-h-screen flex items-center justify-center text-ink-muted font-mono uppercase tracking-[0.3em] text-xs">
    Aligning…
  </div>
);

// Witness · Sākṣī — built May 26 2026, parked for later activation.
// Source: pages/Witness.jsx + components/witness/ + backend/witness.py
// Backend endpoints (/api/witness/*) remain live but undiscoverable.
// To revive: uncomment the import + the two routes below.
// import Witness from "@/pages/Witness";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-ink-muted font-mono">Aligning...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// /app/ask now renders AskV2 unconditionally. The legacy streaming
// Ask.jsx + /ask/stream backend route were retired in Fix 3 (Feb 22
// 2026) once /ask/v2 was stable post Fix 0/1/2. Kept as a separate
// component (vs inlining) so future cohort experiments can route here
// without redoing the Route mounting.
function AskRouter() {
  return <AskV2 />;
}

// ScrollToTop · resets scroll position on every route change. Mount once
// inside <BrowserRouter>. Fixes mid-scroll landings when navigating
// between lessons (Moon → Sun → Śani etc.) especially on mobile where
// browsers otherwise restore the previous route's scroll offset.
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    // `auto` (instant) is intentional — smooth scrolling on route change
    // shows the previous page's content briefly, which reads as a bug.
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);
  return null;
}

// Self-hosted Google OAuth (May 27 2026) — pure query-param return.
// The backend 302-redirects to /auth/google?token=<jwt> after exchanging
// the code at Google. No hash fragments. No sessionStorage stash. Just
// a clean route mapped to AuthCallback below.
function AppRoutes() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/features" element={<Features />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/support" element={<Contact />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/delete-account" element={<DeleteAccount />} />
        {/* Academy — Wave Ārdrā. Public, no auth. Index + first encounter. */}
        <Route path="/learn" element={<Learn />} />
        <Route path="/learn/ardra" element={<LearnArdra />} />
        {/* Bhrigu Vedic Jyotish Academy — cinematic landing experience. */}
        <Route path="/academy" element={<Academy />} />
        <Route element={<AcademyLessonLayout />}>
          <Route path="/academy/moon" element={<Protected><AcademyMoon /></Protected>} />
          <Route path="/academy/dasha" element={<Protected><AcademyDasha /></Protected>} />
          <Route path="/academy/ascendant" element={<Protected><AcademyAscendant /></Protected>} />
          <Route path="/academy/sun" element={<Protected><AcademySun /></Protected>} />
          <Route path="/academy/sani" element={<Protected><AcademySani /></Protected>} />
          <Route path="/academy/mars" element={<Protected><AcademyMars /></Protected>} />
          <Route path="/academy/jupiter" element={<Protected><AcademyJupiter /></Protected>} />
          <Route path="/academy/venus" element={<Protected><AcademyVenus /></Protected>} />
          <Route path="/academy/mercury" element={<Protected><AcademyMercury /></Protected>} />
          <Route path="/academy/nodes" element={<Protected><AcademyNodes /></Protected>} />
        </Route>
        <Route path="/app/vakya" element={<Protected><Vakya /></Protected>} />

        {/* Instagram poster export stage — public, 1080×1350 */}
        <Route path="/poster" element={<Poster />} />
        <Route path="/poster/vakya" element={<PosterVakya />} />
        {/* OAuth callback — query-param return. Path matches what's
            registered in Google Cloud Console: /auth/callback. */}
        <Route path="/auth/callback" element={<AuthCallback />} />
        {/* The Witness · Sākṣī — parked May 26 2026. Files preserved.
            To revive: uncomment the import in this file + these two routes.
            <Route path="/witness" element={<Witness />} />
            <Route path="/sakshi"  element={<Witness />} /> */}
        <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
        <Route path="/admin" element={<Protected><Admin /></Protected>} />
        <Route path="/app" element={<Protected><AppShell /></Protected>}>
          <Route index element={<Dashboard />} />
          <Route path="chart/:id" element={<ChartView />} />
          <Route path="ask" element={<AskRouter />} />
          <Route path="talk" element={<Talk />} />
          <Route path="journal" element={<Journal />} />
          <Route path="profiles" element={<Profiles />} />
          <Route path="billing" element={<Billing />} />
        </Route>
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Toaster position="bottom-right" toastOptions={{ style: { background: "#2E1A0E", color: "#F7E7C4", border: "1px solid #E8562B" } }} />
          <AuthHealthMonitor />
          <KeepaliveMonitor />
          <AppRoutes />
          <PWAInstallBanner />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
